import React, { useCallback } from 'react';
import ReactGA from 'react-ga';
import { MapIcon, MarkerIcon, PhoneIcon, PhoneOutIcon, ProtectIcon } from 'src/components';
import { SText } from 'src/components/SText';
import { currencySymbolMapping } from 'src/helpers';
import { AgencyPublicDataModel } from 'src/types/public/public-data-model.types';

type Props = {
  toAgency: AgencyPublicDataModel;
  restToPay: number;
  currency: string;
  testID?: string;
  code?: string;
};

export const DeliveryWidgetInfo: React.FC<Props> = ({ toAgency, restToPay, currency, code }) => {
  const fullAddress = toAgency.address
    ? `${toAgency.address} ${toAgency.city.name} ${toAgency.zip}`
    : toAgency.city.name;
  const mapLatLang = toAgency.lat && toAgency.lang ? `${toAgency.lat},${toAgency.lang}` : undefined;

  const trackPhoneCall = useCallback(() => {
    ReactGA.event({
      category: 'Receiver',
      action: 'PhoneCallAgency',
      label: `${toAgency.name}_${toAgency.city.name}_${toAgency.name}`,
    });
  }, [toAgency]);

  const trackMapLink = useCallback(() => {
    ReactGA.event({
      category: 'Receiver',
      action: 'MAPAgencyLink',
      label: `${toAgency.city.name}_${toAgency.name}`,
    });
  }, [toAgency]);

  return (
    <div className="bg-white px-2 py-4 mt-2">
      <SText label="public.withdrawn-title" classNames="text-primary font-semibold block text-center text-2xl" />
      <SText label="public.withdrawn-sub-title" classNames="block my-4 text-xl" />

      <div className="mt-4">
        {(!!mapLatLang || !!fullAddress) && (
          <>
            <div className="flex flex-row items-center">
              <MarkerIcon />
              <SText label="public.itinerate" classNames="ml-2 text-primary my-2" />
            </div>

            <a
              className="flex flex-row items-center my-6"
              href={`https://maps.google.com/?q=${mapLatLang ? mapLatLang : fullAddress}`}
              target="_blank"
              rel="noreferrer"
              onClick={trackMapLink}
            >
              <div
                className="w-20 h-20 flex items-center justify-center bg-primary rounded-md"
                style={{
                  width: '110px',
                }}
              >
                <MapIcon />
              </div>
              {!!fullAddress && (
                <SText label={fullAddress} classNames="text-primary ml-2 text-xl" style={{ width: '60%' }} />
              )}
            </a>
          </>
        )}

        {!!toAgency.tel && (
          <div className="my-5">
            <div className="flex flex-row items-center">
              <PhoneOutIcon />
              <SText label="public.phone" classNames="ml-2 text-primary my-2" />
            </div>
            <a className="flex flex-row items-center my-6" href={`tel:${toAgency.tel}`} onClick={trackPhoneCall}>
              <div
                className="w-20 h-20 flex items-center justify-center bg-primary rounded-md "
                style={{
                  width: '110px',
                }}
              >
                <PhoneIcon />
              </div>
              <SText label={toAgency.tel} classNames="text-primary ml-2 text-xl" />
            </a>
          </div>
        )}

        {!!code && (
          <div className="flex flex-row items-center mt-4 justify-between">
            <div>
              <ProtectIcon />
            </div>
            <div>
              <SText label="public.secret-code" classNames=" ml-2 text-primary text-xl" />
            </div>
            <div
              className="bg-primary py-2 px-4 text-primary font-semibold text-xl ml-5 rounded-md text-center"
              style={{ width: '118px' }}
            >
              {code}
            </div>
          </div>
        )}
        {!!restToPay && (
          <div className="flex flex-row items-center mt-4 justify-between">
            <div>
              <SText label="public.rest-to-pay" classNames=" ml-2 text-primary text-xl" />
            </div>
            <div
              className="bg-primary py-2 px-4 text-primary font-semibold text-xl ml-5 rounded-md text-center"
              style={{ width: '118px' }}
            >{`${restToPay.toFixed(2)} ${currencySymbolMapping(currency)}`}</div>
          </div>
        )}
      </div>
    </div>
  );
};
